import {
    keepPreviousData,
    queryOptions,
    useQuery,
  } from '@tanstack/react-query';
  
  import { baseAPI } from '~shared/api/base-api';
  import { QUERY_KEYS } from '~shared/api/query-keys';
  
  export interface AuditLog {
    _id: string;
    resourceType: string;
    resourceId: string;
    resourceName: string;
    /** Populated for `enrollment` rows: class title from Enrollment */
    enrollmentDisplayName?: string;
    /** Populated for `enrollment` rows: student display name */
    studentDisplayName?: string;
    /** When actor is empty (e.g. webhook), parent from metadata.parentId */
    parentActorDisplayName?: string;
    parentActorEmail?: string;
    /** Payment rows: friendly label (event title, or class + session for session fees) */
    paymentDisplayName?: string;
    eventType: string;
    actorId: string;
    actorType: string;
    actorEmail: string;
    actorName: string;
    changes?: {
      before?: Record<string, unknown>;
      after?: Record<string, unknown>;
      fields?: Array<string>;
    };
    metadata: Record<string, unknown>;
    createdAt: string;
  }
  
  export type AuditResourceType = string;
  export type AuditEventType = string;
  export type AuditActorType = string;

  export interface GetAuditLogsParams {
    resourceType?: AuditResourceType;
    eventType?: AuditEventType;
    actorType?: AuditActorType;
    actorId?: string;
    resourceId?: string;
    resourceName?: string;
    startDate?: string;
    endDate?: string;
    studioId?: string;
    source?: string;
    limit?: number; // min: 1, max: 100, default: 20
    offset?: number; // min: 0, default: 0
    sortBy?: 'createdAt' | 'updatedAt' | 'eventType' | 'resourceType'; // default: 'createdAt'
    sortOrder?: 'asc' | 'desc'; // default: 'desc'
  }
  
  export interface GetAuditLogsResponse {
    data: Array<AuditLog>;
    pagination: {
      total: number;
      totalPages: number;
      limit: number;
      offset: number;
    };
  }
  
  export const getAuditLogsAPI = async (params?: GetAuditLogsParams) => {
    const response = await baseAPI.get<GetAuditLogsResponse>(
      `audit-logs`,
      {
        params,
      },
    );
    return response.data;
  };
  
  export const getAuditLogsOptions = (params?: GetAuditLogsParams) => {
    return queryOptions({
      queryKey: QUERY_KEYS.AUDIT_LOGS(params),
      queryFn: () => getAuditLogsAPI(params),
      placeholderData: keepPreviousData,
    });
  };
  
  export const useGetAuditLogs = (params?: GetAuditLogsParams) => {
    return useQuery(getAuditLogsOptions(params));
  };
  